import { useCallback, useEffect, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";

// IP-156 hosted-mock toggle: admin-controlled upstream routing for a native
// REST provider (Simpro). While the override is set, the gateway routes EVERY
// tenant connection's calls - and connect validation - to the override target
// (our hosted mock) instead of the customer's own build. This hook reads the
// routing state and flips it; the PUT body stays empty so the server applies
// its configured mock defaults.
export type UpstreamOverrideState = {
  active: boolean;
  buildUrl: string | null;
  companyId: string | null;
};

export type UseAdminUpstreamOverrideResult = {
  state: UpstreamOverrideState | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  // PUT (route to the mock) / DELETE (route normally). Both re-read the state.
  routeToMock: () => Promise<void>;
  routeToReal: () => Promise<void>;
};

const base = (provider: string): string =>
  `/api/admin/integration-catalog/${encodeURIComponent(provider)}/upstream-override`;

export const useAdminUpstreamOverride = (provider: string): UseAdminUpstreamOverrideResult => {
  const [state, setState] = useState<UpstreamOverrideState | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(base(provider), { credentials: "include" });
      if (!res.ok) throw new Error(`override fetch failed (${res.status})`);
      setState((await res.json()) as UpstreamOverrideState);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const mutate = useCallback(
    async (method: "PUT" | "DELETE"): Promise<void> => {
      const res = await fetch(base(provider), {
        method,
        credentials: "include",
        headers: { ...getCsrfHeader() },
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `routing change failed (${res.status})`);
      }
      await refresh();
    },
    [provider, refresh],
  );

  const routeToMock = useCallback(() => mutate("PUT"), [mutate]);
  const routeToReal = useCallback(() => mutate("DELETE"), [mutate]);

  return { state, loading, error, refresh, routeToMock, routeToReal };
};
